/** FF007 — pneumoconiosis checkup chat steps (after work history review). */

import type { CareerHistorySurveyStep, PneuCheckupDraft, YesNo } from './careerHistorySurveyTypes';
import { emptyPneuSlotDraft } from './careerHistorySurveyTypes';

export const PNEU_GATE_STEP = 'pneuGate';
export const PNEU_SECTION_COMPLETE_STEP = 'pneuSectionComplete';

export type PneuCheckupStep =
  | typeof PNEU_GATE_STEP
  | 'pneu1Date'
  | 'pneu1Contractor'
  | 'pneu1Findings'
  | 'pneu1Area'
  | 'pneu2Date'
  | 'pneu2Contractor'
  | 'pneu2Findings'
  | 'pneu2Area'
  | typeof PNEU_SECTION_COMPLETE_STEP;

export const PNEU_FIELD_STEPS: PneuCheckupStep[] = [
  'pneu1Date',
  'pneu1Contractor',
  'pneu1Findings',
  'pneu1Area',
  'pneu2Date',
  'pneu2Contractor',
  'pneu2Findings',
  'pneu2Area',
];

export const PNEU_STEPS: PneuCheckupStep[] = [PNEU_GATE_STEP, ...PNEU_FIELD_STEPS, PNEU_SECTION_COMPLETE_STEP];

export function isPneuStep(step: CareerHistorySurveyStep): step is PneuCheckupStep {
  return PNEU_STEPS.includes(step as PneuCheckupStep);
}

export function pneuStepQuestionKey(step: PneuCheckupStep): string {
  return `submitDocuments.careerHistorySurvey.steps.${step}`;
}

export function nextPneuStepAfter(current: PneuCheckupStep, gateAnswer: YesNo | ''): CareerHistorySurveyStep | null {
  switch (current) {
    case 'pneuGate':
      return gateAnswer === 'yes' ? 'pneu1Date' : PNEU_SECTION_COMPLETE_STEP;
    case 'pneu1Date':
      return 'pneu1Contractor';
    case 'pneu1Contractor':
      return 'pneu1Findings';
    case 'pneu1Findings':
      return 'pneu1Area';
    case 'pneu1Area':
      return 'pneu2Date';
    case 'pneu2Date':
      return 'pneu2Contractor';
    case 'pneu2Contractor':
      return 'pneu2Findings';
    case 'pneu2Findings':
      return 'pneu2Area';
    case 'pneu2Area':
      return PNEU_SECTION_COMPLETE_STEP;
    default:
      return null;
  }
}

export function prevPneuStepForUndo(current: PneuCheckupStep, draft: PneuCheckupDraft): PneuCheckupStep | null {
  switch (current) {
    case 'pneuGate':
      return null;
    case 'pneu1Date':
      return 'pneuGate';
    case 'pneu1Contractor':
      return 'pneu1Date';
    case 'pneu1Findings':
      return 'pneu1Contractor';
    case 'pneu1Area':
      return 'pneu1Findings';
    case 'pneu2Date':
      return 'pneu1Area';
    case 'pneu2Contractor':
      return 'pneu2Date';
    case 'pneu2Findings':
      return 'pneu2Contractor';
    case 'pneu2Area':
      return 'pneu2Findings';
    case 'pneuSectionComplete':
      if (draft.slot2Skipped || !isSlot2Started(draft)) return 'pneu1Area';
      return 'pneu2Area';
    default:
      return null;
  }
}

function isSlot2Started(draft: PneuCheckupDraft): boolean {
  return Boolean(
    draft.slot2.checkupDate ||
      draft.slot2.contractor.trim() ||
      draft.slot2.findings ||
      draft.slot2.area.trim()
  );
}

export function isPneuFieldAnswered(step: PneuCheckupStep, draft: PneuCheckupDraft): boolean {
  switch (step) {
    case 'pneuGate':
      return draft.gateAnswer === 'yes' || draft.gateAnswer === 'no';
    case 'pneu1Date':
      return Boolean(draft.slot1.checkupDate);
    case 'pneu1Contractor':
      return Boolean(draft.slot1.contractor.trim());
    case 'pneu1Findings':
      return draft.slot1.findings === 'yes' || draft.slot1.findings === 'no';
    case 'pneu1Area':
      return Boolean(draft.slot1.area.trim());
    case 'pneu2Date':
      return draft.slot2Skipped || Boolean(draft.slot2.checkupDate);
    case 'pneu2Contractor':
      return draft.slot2Skipped || Boolean(draft.slot2.contractor.trim());
    case 'pneu2Findings':
      return draft.slot2Skipped || draft.slot2.findings === 'yes' || draft.slot2.findings === 'no';
    case 'pneu2Area':
      return draft.slot2Skipped || Boolean(draft.slot2.area.trim());
    case 'pneuSectionComplete':
      return true;
    default:
      return false;
  }
}

export function committedPneuStepsForDraft(draft: PneuCheckupDraft): Set<PneuCheckupStep> {
  const set = new Set<PneuCheckupStep>();
  for (const s of PNEU_STEPS) {
    if (isPneuFieldAnswered(s, draft)) set.add(s);
  }
  return set;
}

/** Clear answers from `from` through the rest of the pneu field steps (for undo). */
export function clearPneuDraftFromStep(draft: PneuCheckupDraft, from: PneuCheckupStep): PneuCheckupDraft {
  const next: PneuCheckupDraft = {
    ...draft,
    slot1: { ...draft.slot1 },
    slot2: { ...draft.slot2 },
    slot2Skipped: draft.slot2Skipped,
  };
  const fromIdx = PNEU_FIELD_STEPS.indexOf(from);
  if (fromIdx < 0) return next;

  const slot2Start = PNEU_FIELD_STEPS.indexOf('pneu2Date');
  if (fromIdx >= slot2Start) {
    next.slot2Skipped = false;
  }

  for (let i = fromIdx; i < PNEU_FIELD_STEPS.length; i++) {
    switch (PNEU_FIELD_STEPS[i]) {
      case 'pneu1Date':
        next.slot1.checkupDate = '';
        break;
      case 'pneu1Contractor':
        next.slot1.contractor = '';
        break;
      case 'pneu1Findings':
        next.slot1.findings = '';
        break;
      case 'pneu1Area':
        next.slot1.area = '';
        break;
      case 'pneu2Date':
        next.slot2 = emptyPneuSlotDraft();
        break;
      case 'pneu2Contractor':
        next.slot2.contractor = '';
        break;
      case 'pneu2Findings':
        next.slot2.findings = '';
        break;
      case 'pneu2Area':
        next.slot2.area = '';
        break;
      default:
        break;
    }
  }
  return next;
}

export function buildPneuResumeStep(
  sectionWorkHistoryDone: boolean,
  sectionPneuDone: boolean,
  currentStepId: string | null | undefined,
  pneuCheckup: boolean | null | undefined
): CareerHistorySurveyStep {
  if (sectionPneuDone) return PNEU_SECTION_COMPLETE_STEP;
  const step = (currentStepId ?? '').trim() as CareerHistorySurveyStep;
  if (isPneuStep(step)) return step;
  if (sectionWorkHistoryDone) {
    if (pneuCheckup === false) return PNEU_SECTION_COMPLETE_STEP;
    if (pneuCheckup === true && step && step !== 'workHistoryReview') return step;
    return PNEU_GATE_STEP;
  }
  return step || 'workHistoryIntro';
}
